import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { formatDate } from '@/utils/dateFormat';
import { UserPlus, Copy, Link2, Ban, Smartphone, KeyRound, Loader2 } from 'lucide-react';

const publicUrl = (audit) => `${window.location.origin}/audit/${audit.id}`;

const StaffCard = ({ s, selfie, audit, onDeactivate }) => (
  <div className={`rounded-lg border p-3 flex gap-3 ${s.active === false ? 'opacity-50 bg-gray-50' : 'bg-white'}`} data-testid={`audit-staff-${s.id}`}>
    {selfie ? <img src={selfie} alt={s.name} className="w-14 h-14 rounded-md object-cover border" data-testid={`audit-staff-selfie-${s.id}`} />
      : <div className="w-14 h-14 rounded-md bg-gray-100 border flex items-center justify-center text-gray-400 text-xs">no selfie</div>}
    <div className="flex-1 min-w-0 text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm text-gray-800">{s.name}</span>
        <Badge className={s.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>{s.active === false ? 'disabled' : s.status}</Badge>
        {audit.links_active && audit.status !== 'approved' && audit.status !== 'cancelled' && s.active !== false && (
          <span className="font-mono bg-gray-900 text-white px-2 py-0.5 rounded flex items-center gap-1" data-testid={`audit-staff-pin-${s.id}`}><KeyRound className="w-3 h-3" />{s.pin}</span>
        )}
      </div>
      <div className="text-gray-500 mt-1 flex items-center gap-1 truncate"><Smartphone className="w-3 h-3 shrink-0" /><span className="truncate" title={s.device_info || ''}>{s.device_id ? `${s.device_id.slice(0, 14)}… · ${s.device_info || ''}` : 'device not yet registered'}</span></div>
      <div className="text-gray-500 mt-0.5">{s.entries || 0} entries{s.onboarded_at ? ` · started ${formatDate(s.onboarded_at)}` : ''}{s.last_seen ? ` · last seen ${formatDate(s.last_seen)}` : ''}{(s.devices || []).length > 1 ? ` · ${s.devices.length} devices` : ''}</div>
    </div>
    {s.active !== false && audit.status !== 'approved' && audit.status !== 'cancelled' && (
      <Button size="icon" variant="ghost" className="text-red-500" title="Disable this PIN" onClick={() => onDeactivate(s)} data-testid={`audit-staff-disable-${s.id}`}><Ban className="w-4 h-4" /></Button>
    )}
  </div>
);

export const AuditStaffPanel = ({ audit, onChanged }) => {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState('');
  const [selfies, setSelfies] = useState({});
  const url = publicUrl(audit);
  const finalState = audit.status === 'approved' || audit.status === 'cancelled';
  const closed = finalState || !audit.links_active;

  useEffect(() => { QRCode.toDataURL(url, { width: 160, margin: 1 }).then(setQr).catch(() => setQr('')); }, [url]);
  const selfieKey = (audit.staff || []).filter(s => s.has_selfie).map(s => s.id).join(',');
  useEffect(() => { if (!selfieKey) return; api.get(`/stock-audit/${audit.id}/staff-selfies`).then(r => setSelfies(r.data || {})).catch(() => {}); }, [audit.id, selfieKey]);

  const add = async () => {
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await api.post(`/stock-audit/${audit.id}/staff`, { name: name.trim() });
      toast({ title: `PIN for ${r.data.name}: ${r.data.pin}`, description: 'Share the audit link and this PIN with them.' });
      setName(''); onChanged();
    } catch (e) { toast({ title: 'Could not add staff', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const deactivate = async (s) => {
    if (!window.confirm(`Disable ${s.name}'s PIN? They will no longer be able to count.`)) return;
    await api.delete(`/stock-audit/${audit.id}/staff/${s.id}`); onChanged();
  };
  const toggleLinks = async () => {
    const path = closed ? 'reopen-links' : 'expire-links';
    if (!closed && !window.confirm('Expire the audit link for every staff member now?')) return;
    try { await api.post(`/stock-audit/${audit.id}/${path}`); onChanged(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };
  const copy = () => { navigator.clipboard?.writeText(url); toast({ title: 'Link copied' }); };

  return (
    <Card data-testid="audit-staff-panel">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4 text-indigo-600" /> Staff counting link & PINs
        <Badge className={closed ? 'bg-gray-200 text-gray-700' : 'bg-emerald-100 text-emerald-800'} data-testid="audit-links-status">{closed ? 'link expired' : 'link active'}</Badge></CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-[180px_1fr] gap-4">
        <div className="text-center">
          {qr && <img src={qr} alt="Audit link QR" className="mx-auto border rounded" />}
          <div className="text-[11px] text-gray-500 break-all mt-1" data-testid="audit-public-url">{url}</div>
          <div className="flex gap-1 justify-center mt-2">
            <Button size="sm" variant="outline" onClick={copy} data-testid="audit-copy-link"><Copy className="w-3 h-3 mr-1" />Copy</Button>
            {!finalState && <Button size="sm" variant="outline" onClick={toggleLinks} data-testid="audit-toggle-links">{closed ? 'Reopen link' : 'Expire link'}</Button>}
          </div>
        </div>
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Each staff member gets their own PIN. On first use they enter their name, take a selfie and their device is registered. Every count they enter is logged with their name, time and device. The link expires automatically once HQ approves the audit.</p>
          {!finalState && (
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Staff name" onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="audit-staff-name" />
              <Button onClick={add} disabled={busy || name.trim().length < 2} className="bg-indigo-600 hover:bg-indigo-700" data-testid="audit-staff-add">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />} Add staff & PIN</Button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            {(audit.staff || []).length === 0 && <p className="text-sm text-gray-400">No staff added yet.</p>}
            {(audit.staff || []).map(s => <StaffCard key={s.id} s={s} selfie={selfies[s.id]} audit={audit} onDeactivate={deactivate} />)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AuditStaffPanel;
